<template>
  <div class="space-y-4" data-test-id="merchant-overview">
    <!-- Compact header strip -->
    <section
      class="bg-surface-0 dark:bg-surface-900 p-4 rounded-lg"
      data-test-id="merchant-overview-header"
    >
      <div v-if="isLoading" class="space-y-2">
        <Skeleton width="14rem" height="1.75rem" />
        <Skeleton width="36rem" height="1rem" />
      </div>
      <div
        v-else-if="merchant"
        class="flex flex-wrap items-center gap-x-4 gap-y-2"
      >
        <BadgeFccSeverity
          :value="formatTolerance(merchant.TOLERANCE_ID)"
          :backgroundColor="
            getToleranceColor(merchant.TOLERANCE_ID).backgroundColor
          "
          :textColor="getToleranceColor(merchant.TOLERANCE_ID).textColor"
          :borderColor="getToleranceColor(merchant.TOLERANCE_ID).borderColor"
          data-test-id="merchant-tolerance"
        />
        <h2
          class="text-xl font-semibold text-surface-900 dark:text-surface-0"
          data-test-id="merchant-name"
        >
          {{ merchant.MERCHANT_NAME }}
        </h2>
        <div
          class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-surface-600 dark:text-surface-400"
        >
          <span data-test-id="merchant-merchant-id">
            <span class="text-surface-500">ID</span>
            <span
              class="ml-1 font-medium text-surface-800 dark:text-surface-200"
              >{{ merchant.MERCHANT_ID }}</span
            >
          </span>
          <span data-test-id="merchant-owner">
            <span class="text-surface-500">Owner</span>
            <span
              class="ml-1 font-medium text-surface-800 dark:text-surface-200"
              >{{ merchant.MERCHANT_OWNER }}</span
            >
          </span>
          <span data-test-id="merchant-mcc">
            <span class="text-surface-500">MCC</span>
            <span
              class="ml-1 font-medium text-surface-800 dark:text-surface-200"
              >{{ merchant.MCC }}</span
            >
          </span>
          <span data-test-id="merchant-platform">
            <span class="text-surface-500">Platform</span>
            <span
              class="ml-1 font-medium text-surface-800 dark:text-surface-200"
              >{{ formatPlatform(merchant.PLATFORM) }}</span
            >
          </span>
          <span data-test-id="merchant-go-live-date">
            <span class="text-surface-500">Go-Live</span>
            <span
              class="ml-1 font-medium text-surface-800 dark:text-surface-200"
              >{{ formatDate(merchant.GO_LIVE_DATE) }}</span
            >
          </span>
        </div>
      </div>
    </section>

    <!-- Two-column dashboard: Risk Dashboard | Key Metrics -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Risk Signals -->
      <section
        class="bg-surface-0 dark:bg-surface-900 p-4 rounded-lg"
        data-test-id="merchant-risk-signals"
      >
        <div class="flex items-baseline justify-between mb-3">
          <h3 class="font-semibold text-surface-900 dark:text-surface-0">
            Risk Signals
          </h3>
          <span
            v-if="
              !isLoading &&
              merchantStore.merchantAlertHistory.length > 0 &&
              merchantStore.merchantAlertHistory.length <
                riskSignals.alertTotal
            "
            class="text-[11px] text-surface-500 dark:text-surface-400"
          >
            Last {{ merchantStore.merchantAlertHistory.length }} of
            {{ riskSignals.alertTotal }} alerts
          </span>
        </div>

        <div v-if="isLoading" class="grid grid-cols-3 gap-2">
          <Skeleton v-for="i in 6" :key="i" width="100%" height="4.25rem" />
        </div>

        <div v-else class="space-y-2" data-test-id="risk-alerts-block">
          <!-- Severity breakdown row -->
          <div
            :class="[
              'grid gap-2',
              riskSignals.severityCounts.unknown > 0
                ? 'grid-cols-2 sm:grid-cols-4'
                : 'grid-cols-3',
            ]"
          >
            <RiskSignalTile
              label="High"
              :value="riskSignals.severityCounts.high"
              tone="red"
              data-test-id="risk-alerts-high"
            />
            <RiskSignalTile
              label="Medium"
              :value="riskSignals.severityCounts.medium"
              tone="orange"
              data-test-id="risk-alerts-medium"
            />
            <RiskSignalTile
              label="Low"
              :value="riskSignals.severityCounts.low"
              tone="green"
              data-test-id="risk-alerts-low"
            />
            <RiskSignalTile
              v-if="riskSignals.severityCounts.unknown > 0"
              label="Unspecified"
              :value="riskSignals.severityCounts.unknown"
              tone="neutral"
              data-test-id="risk-alerts-unknown"
            />
          </div>

          <!-- State row -->
          <div class="grid grid-cols-3 gap-2">
            <RiskSignalTile
              label="Unactioned Alerts"
              :value="riskSignals.unactionedCount"
              tone="neutral"
              data-test-id="risk-unactioned"
            />
            <RiskSignalTile
              label="All Events"
              :value="riskSignals.currentEventsCount"
              tone="neutral"
              data-test-id="risk-current-events"
            />
            <!-- Reserve metrics load via a separate fetch, so skeleton the
                 tile until it settles — avoids a misleading "Off" flash
                 before the real Active/Off value arrives. On a genuine fetch
                 error we fall through to the tile (shows "Off"). -->
            <Skeleton
              v-if="reserveTileLoading"
              width="100%"
              height="4.25rem"
              data-test-id="risk-reserve-loading"
            />
            <RiskSignalTile
              v-else
              label="Reserve"
              :value="reserveStatus.value"
              :active="reserveStatus.active"
              tone="green"
              data-test-id="risk-reserve"
            />
          </div>

          <!-- Settlement hold. Its own row rather than a fourth state tile:
               it is the one signal here that reports a deliberate operator
               action rather than a count the system observed. Same 3-column
               grid as the rows above so it matches their width. -->
          <div v-if="settlementStatus" class="grid grid-cols-3 gap-2">
            <RiskSignalTile
              label="Settlements"
              :value="settlementStatus.value"
              :active="settlementStatus.active"
              :tone="settlementStatus.tone"
              :data-settlement-state="settlementStatus.state"
              data-test-id="risk-settlement-hold"
            />
          </div>
        </div>
      </section>

      <!-- Key Metrics -->
      <section
        class="bg-surface-0 dark:bg-surface-900 p-4 rounded-lg"
        data-test-id="merchant-key-metrics"
      >
        <h3 class="font-semibold text-surface-900 dark:text-surface-0 mb-3">
          Key Metrics
        </h3>
        <div v-if="!keyMetrics.length" class="grid grid-cols-2 gap-2">
          <Skeleton v-for="i in 4" :key="i" width="100%" height="4.25rem" />
        </div>
        <dl v-else class="grid grid-cols-2 gap-2">
          <div
            v-for="metric in keyMetrics"
            :key="metric.label"
            :class="[
              'relative rounded-md px-3 py-2.5 flex flex-col gap-1.5 border bg-surface-50 dark:bg-surface-800/60',
              metric.accentBorder ?? 'border-transparent',
            ]"
          >
            <dt
              class="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider font-medium text-surface-500 dark:text-surface-400"
            >
              <span class="truncate">{{ metric.label }}</span>
            </dt>
            <dd
              :data-test-id="getMetricTestId(metric.originalKey)"
              :class="[
                'text-xl font-semibold leading-none tracking-tight tabular-nums',
                metric.colorClass ?? 'text-surface-900 dark:text-surface-0',
              ]"
            >
              {{ metric.value }}
            </dd>
          </div>
        </dl>
      </section>
    </div>

    <!-- Reserve Metrics -->
    <section
      class="bg-surface-0 dark:bg-surface-900 p-4 rounded-lg"
      data-test-id="merchant-reserve-metrics"
    >
      <h3 class="font-semibold text-surface-900 dark:text-surface-0 mb-3">
        Reserve Metrics
      </h3>
      <div
        v-if="isLoading || merchantStore.reserveMetricsLoading"
        class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
      >
        <Skeleton v-for="i in 7" :key="i" width="100%" height="4.25rem" />
      </div>
      <dl
        v-else
        class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
      >
        <div
          v-for="metric in reserveMetrics"
          :key="metric.key"
          class="relative rounded-md px-3 py-2.5 flex flex-col gap-1.5 border border-transparent bg-surface-50 dark:bg-surface-800/60"
        >
          <dt
            class="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider font-medium text-surface-500 dark:text-surface-400"
          >
            <span class="truncate">{{ metric.label }}</span>
          </dt>
          <dd
            :data-test-id="`reserve-metric-${metric.key}`"
            class="text-xl font-semibold leading-none tracking-tight tabular-nums text-surface-900 dark:text-surface-0"
          >
            {{ metric.value }}
          </dd>
        </div>
      </dl>
    </section>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Skeleton from 'primevue/skeleton'
import BadgeFccSeverity from '@/components/common/badges/BadgeFccSeverity.vue'
import RiskSignalTile from '@/components/fcc/merchantdetails/RiskSignalTile.vue'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import { formatUpperUnderscoreCase } from '@/util/util'
import { Merchant, KeyMetric } from '@/types/fcc'
import { useFCCMerchantStore } from '@/stores/fcc/fccMerchantStore'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

const CHARGEBACK_RATE_WARN_THRESHOLD = 0.01

const REFUND_RATE_WARN_THRESHOLD = 0.05

interface Props {
  merchant: Merchant | null
  isLoading: boolean
}

const props = defineProps<Props>()

const merchantStore = useFCCMerchantStore()
const { hasPerms } = usePerms()
// Named like MerchantDetailsView's canEdit/canManageSettlements. Kept out of
// the computed below so the site does not read as an early-return fetch guard:
// it gates rendering, and the fetch it depends on is guarded in the view.
const canReadSettlementHold = computed(() =>
  hasPerms(PERMISSIONS.NETSUITE_SETTLEMENT_READ),
)
// Reserve Metrics tiles + Key Metrics rely on the centralised formatters from
// useFCCFormatting.
const {
  formatPlatform,
  formatTolerance,
  getToleranceColor,
  formatDate,
  formatKeyMetric,
} = useFCCFormatting()

// The state lives in the value, not the label: at a third of the row the label
// truncates, and a cut "Settlements Held" above "On" reads as the opposite of
// what it means. Grey while
// the NetSuite read is in flight so a slow Workato response shows as pending
// rather than as no hold. Absent when the flag is unknown for good (no vendor
// name, or the read failed) — the dialog carries that story, and a tile cannot
// say "unknown" without implying a state.
const settlementStatus = computed<{
  state: 'held' | 'active' | 'checking'
  value: string
  tone: 'amber' | 'green' | 'neutral'
  active: boolean
} | null>(() => {
  if (!canReadSettlementHold.value) return null
  if (merchantStore.holdPaymentLoading) {
    return {
      state: 'checking',
      value: 'Checking',
      tone: 'neutral',
      active: false,
    }
  }
  const held = merchantStore.holdPayment
  if (held === null) return null
  return held
    ? { state: 'held', value: 'Held', tone: 'amber', active: true }
    : { state: 'active', value: 'Active', tone: 'green', active: true }
})

const severityCounts = computed(() => {
  const counts = { high: 0, medium: 0, low: 0, unknown: 0 }
  for (const alert of merchantStore.merchantAlertHistory) {
    const tolerance = (alert.TOLERANCE || '').toUpperCase()
    if (tolerance === 'HIGH') counts.high++
    else if (tolerance === 'MEDIUM') counts.medium++
    else if (tolerance === 'LOW') counts.low++
    else counts.unknown++
  }
  return counts
})

const riskSignals = computed(() => ({
  alertTotal: merchantStore.alertHistoryPagination.total,
  severityCounts: severityCounts.value,
  unactionedCount: merchantStore.merchantAlertHistory.filter(a => !a.ACTIONED)
    .length,
  currentEventsCount: props.merchant?.CURRENT_EVENTS?.length ?? 0,
}))

// Reserve status is derived from the /reserve/metrics record — the single
// source of truth shared with the Reserve Metrics section and the Edit
// Reserves dialog gating. "Has a reserve" = a record exists (200, non-null);
// 404 → null → no reserve. Two-state: Active only when a reserve exists and
// is not disabled; everything else (no reserve, or disabled) → Off.
const reserveStatus = computed<{ value: string; active: boolean }>(() => {
  const r = merchantStore.reserveMetrics
  const hasReserve = merchantStore.reserveMetricsLoaded && r !== null
  const disabled = !!r?.DISABLED_ON
  const active = hasReserve && !disabled
  return { value: active ? 'Active' : 'Off', active }
})

// Show a skeleton for the Reserve tile until the metrics fetch settles, so
// the user never sees a stale/default "Off" before the real value loads. On
// a genuine fetch error we stop skeletoning and render the tile (→ "Off").
const reserveTileLoading = computed(
  () =>
    !merchantStore.reserveMetricsLoaded && !merchantStore.reserveMetricsError,
)

const keyMetrics = computed(
  (): (KeyMetric & {
    originalKey: string
    colorClass?: string
    accentBorder?: string
  })[] => {
    if (!props.merchant) return []

    const metrics: (KeyMetric & {
      originalKey: string
      colorClass?: string
      accentBorder?: string
    })[] = []

    if (props.merchant.KEY_METRICS) {
      const keyMetricEntries = Object.entries(props.merchant.KEY_METRICS).map(
        ([key, value]) => {
          const numeric = toNumber(value as unknown as string | number)
          return {
            label: formatUpperUnderscoreCase(key),
            value: formatKeyMetric(
              key,
              numeric ?? (value as unknown as string | number),
            ),
            originalKey: key,
            colorClass: getMetricColorClass(key, numeric),
            accentBorder: getMetricAccentBorder(key, numeric),
          }
        },
      )
      metrics.push(...keyMetricEntries)
    }

    return metrics
  },
)

const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return isFinite(value) ? value : null
  const parsed = Number(value)
  return isNaN(parsed) ? null : parsed
}

// The backend sends 0 for null numeric reserve fields, so 0 is treated as
// "no value" (N/A) to avoid misleading 0.00 / 0% on merchants without a
// reserve. Amounts are prefixed with the reserve's currency code (e.g.
// "GBP 100,000.00"); when the currency is unknown they render bare, never
// assuming a currency that might be wrong.
const formatReserveAmount = (
  value: number | undefined | null,
  currencyCode: string | null | undefined,
): string => {
  if (value === null || value === undefined || value === 0) return 'N/A'
  const amount = value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return currencyCode ? `${currencyCode} ${amount}` : amount
}

const formatPercent = (value: number | undefined | null): string => {
  if (value === null || value === undefined || value === 0) return 'N/A'
  return `${value}%`
}

const formatNullableDate = (value: string | null | undefined): string => {
  if (!value) return 'N/A'
  return formatDate(value)
}

const reserveMetrics = computed(() => {
  const r = merchantStore.reserveMetrics
  const currency = r?.DEFAULT_CURRENCY
  return [
    {
      key: 'default-currency',
      label: 'Default Currency',
      value: currency || 'N/A',
    },
    {
      key: 'max-amount',
      label: 'Max Amount',
      value: formatReserveAmount(r?.MAX_AMOUNT, currency),
    },
    {
      key: 'override-max-amount',
      label: 'Override Max Amount',
      value: formatReserveAmount(r?.OVERRIDE_MAX_AMOUNT, currency),
    },
    {
      key: 'amount-collected',
      label: 'Amount Collected',
      value: formatReserveAmount(r?.AMOUNT_COLLECTED, currency),
    },
    {
      key: 'percent-holdback',
      label: 'Percent Holdback',
      value: formatPercent(r?.PERCENT_HOLDBACK),
    },
    {
      key: 'last-updated-date',
      label: 'Last Updated Date',
      value: formatNullableDate(r?.LAST_UPDATED_DATE),
    },
    {
      key: 'disabled-on',
      label: 'Disabled On',
      value: formatNullableDate(r?.DISABLED_ON),
    },
  ]
})

const getMetricColorClass = (
  key: string,
  value: number | null,
): string | undefined => {
  if (value === null) return undefined
  if (key === 'CHARGEBACK_RATE' && value > CHARGEBACK_RATE_WARN_THRESHOLD) {
    return 'text-red-600 dark:text-red-400'
  }
  if (key === 'REFUND_RATE' && value > REFUND_RATE_WARN_THRESHOLD) {
    return 'text-amber-600 dark:text-amber-400'
  }
  return undefined
}

const getMetricAccentBorder = (
  key: string,
  value: number | null,
): string | undefined => {
  if (value === null) return undefined
  if (key === 'CHARGEBACK_RATE' && value > CHARGEBACK_RATE_WARN_THRESHOLD) {
    return 'border-red-500/30 dark:border-red-500/30'
  }
  if (key === 'REFUND_RATE' && value > REFUND_RATE_WARN_THRESHOLD) {
    return 'border-amber-500/30 dark:border-amber-500/30'
  }
  return undefined
}

function getMetricTestId(originalKey: string): string {
  return originalKey
    ? `metric-${originalKey.toLowerCase().replace(/_/g, '-')}`
    : ''
}
</script>
